'use client';

import { cva } from 'class-variance-authority';
import { useRef, useCallback } from 'react';
import { Button } from '@/app/_components/core/button';
import { DownloadButton } from '@/app/_components/core/download-button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/app/_components/core/navigation-menu';
import { SmartLink } from '@/app/_components/core/smart-link';
import { LanguageSwitcherComponent } from '@/app/_components/shared/header/language-switcher';
import { useLabel } from '@/app/_contexts/labels-context';
import { getDownloadUrl, getFilenameFromUrl } from '@/app/_lib/download-url';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import { NavbarProps } from './navbar.types';

const navigationMenuTriggerStyle = cva(
  'cursor-pointer text-foreground font-normal focus:bg-transparent focus:text-primary data-[state=open]:focus:bg-transparent rounded-none bg-transparent hover:bg-transparent hover:text-primary data-[state=open]:hover:bg-transparent data-[state=open]:text-primary data-[state=open]:bg-transparent'
);

const defaultNavigationMenuContentClasses =
  'min-w-xs p-2 border-none group-data-[viewport=false]/navigation-menu:top-11';

const designTokenNavigationMenuContentClasses = [
  'group-data-[viewport=false]/navigation-menu:bg-navbar-int-background',
  'group-data-[viewport=false]/navigation-menu:rounded-navigation-menu-radius',
  'group-data-[viewport=false]/navigation-menu:shadow-navigation-menu-shadow-mobile',
  'lg:group-data-[viewport=false]/navigation-menu:shadow-navigation-menu-shadow-desktop',
  'group-data-[viewport=false]/navigation-menu:mt-navbar-navigation-menu-popover-padding-y-top',
  'group-data-[viewport=false]/navigation-menu:border-solid',
  'group-data-[viewport=false]/navigation-menu:border-navigation-menu-popover-border',
  'backdrop-blur-navigation-menu-blur',
].join(' ');

const navigationMenuContentClasses = cn(
  defaultNavigationMenuContentClasses,
  designTokenNavigationMenuContentClasses
);

export default function DesktopNavigation({
  pages,
  currentPath,
  languages = [],
  variant,
  actionButton,
  showLanguageSwitcher = true,
}: NavbarProps) {
  const overviewLabel = useLabel('overview', 'Overview');

  // Prevent hover/click conflict on desktop navigation.
  // When entering a trigger from outside, click is blocked until the
  // flyout open-animation completes. After that, click freely toggles.
  // Leaving the trigger resets the lock.
  const clickAllowed = useRef(false);
  const clickTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleTriggerPointerEnter = useCallback(() => {
    clickAllowed.current = false;
  }, []);

  const handleTriggerPointerLeave = useCallback(() => {
    clickAllowed.current = false;
    clearTimeout(clickTimer.current);
  }, []);

  const handleContentAnimationEnd = useCallback((e: React.AnimationEvent) => {
    // Only unlock click after the OPEN animation, not the close animation.
    // Small buffer so the flyout feels "settled" before click is allowed.
    clearTimeout(clickTimer.current);
    if (e.currentTarget.getAttribute('data-state') === 'open') {
      clickTimer.current = setTimeout(() => {
        clickAllowed.current = true;
      }, 150);
    }
  }, []);

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    const canHover = window.matchMedia('(hover: hover)').matches;
    if (!canHover || e.detail === 0) return;
    if (!clickAllowed.current) e.preventDefault();
  }, []);

  // Compute tokenized navbar classes
  const getDefaultNavClasses = () => 'flex-1 flex';

  const getDesignTokenNavClasses = () => 'justify-navbar-menu-align-desktop';

  const getNavClasses = () => {
    return cn(getDefaultNavClasses(), getDesignTokenNavClasses());
  };

  // Compute tokenized action button classes */
  const getDefaultActionButtonClasses = () => '';

  const getDesignTokenActionButtonClasses = () =>
    'rounded-bl-navbar-primary-button-left-radius rounded-tl-navbar-primary-button-left-radius rounded-br-navbar-primary-button-right-radius rounded-tr-navbar-primary-button-right-radius h-navbar-primary-button-height';

  const getActionButtonClasses = () => {
    return cn(
      getDefaultActionButtonClasses(),
      getDesignTokenActionButtonClasses()
    );
  };

  return (
    <>
      {variant && variant === 'default' && (
        <div className="hidden w-full items-center justify-between lg:flex">
          {/* Centered Navigation */}
          <div className={getNavClasses()}>
            <NavigationMenu viewport={false} delayDuration={100} skipDelayDuration={50}>
              <NavigationMenuList className="gap-1">
                {pages.map((page, navIndex) => {
                  const hasChildren = page.children && page.children.length > 0;
                  const pageHref = `/${page.slug}`;
                  const isActive =
                    currentPath === pageHref ||
                    currentPath?.startsWith(`${pageHref}/`);
                  // Strict match for aria-current: only the actually displayed
                  // page is "current". Branch ancestors stay highlighted but
                  // unmarked, so AT users hear one current item, not a trail.
                  const isCurrent = currentPath === pageHref;

                  if (hasChildren) {
                    return (
                      <NavigationMenuItem key={page.id}>
                        <NavigationMenuTrigger
                          className={cn(
                            navigationMenuTriggerStyle(),
                            'cursor-default',
                            isActive && 'text-primary'
                          )}
                          onPointerEnter={handleTriggerPointerEnter}
                          onPointerLeave={handleTriggerPointerLeave}
                          onClick={handleTriggerClick}
                          aria-current={isCurrent ? 'page' : undefined}
                          data-track-id={`header-nav-${navIndex + 1}-toggle`}
                        >
                          {page.title}
                        </NavigationMenuTrigger>

                        {/*forceMount*/}
                        <NavigationMenuContent
                          className={navigationMenuContentClasses}
                          onAnimationEnd={handleContentAnimationEnd}
                        >
                          <ul className="m-0 p-0">
                            {/* Overview link - always visible.
                                aria-current sits on the top-level trigger
                                (which is in the DOM even when collapsed),
                                so we don't repeat it here. */}
                            <li>
                              <NavigationMenuLink
                                asChild
                                className={cn(
                                  navigationMenuTriggerStyle(),
                                  isActive && 'text-primary'
                                )}
                              >
                                <SmartLink
                                  href={pageHref}
                                  target={emptyToUndefined(page.linkTarget)}
                                  download={page.download}
                                  data-track-id={`header-nav-${navIndex + 1}-overview`}
                                >
                                  {overviewLabel}
                                </SmartLink>
                              </NavigationMenuLink>
                            </li>

                            {page.children?.map((child, childIndex) => {
                              const childHref = `/${child.slug}`;
                              const isChildActive =
                                currentPath === childHref ||
                                currentPath?.startsWith(`${childHref}/`);
                              const isChildCurrent = currentPath === childHref;

                              return (
                                <li key={child.id}>
                                  <NavigationMenuLink
                                    asChild
                                    className={cn(
                                      navigationMenuTriggerStyle(),
                                      isChildActive && 'text-primary'
                                    )}
                                  >
                                    <SmartLink
                                      href={child.slug}
                                      target={emptyToUndefined(child.linkTarget)}
                                      download={child.download}
                                      aria-current={isChildCurrent ? 'page' : undefined}
                                      data-track-id={`header-nav-${navIndex + 1}-sub-${childIndex + 1}`}
                                    >
                                      {child.title}
                                    </SmartLink>
                                  </NavigationMenuLink>
                                </li>
                              );
                            })}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  }

                  return (
                    <NavigationMenuItem key={page.id}>
                      <NavigationMenuLink
                        asChild
                        className={cn(
                          navigationMenuTriggerStyle(),
                          isActive && 'text-primary'
                        )}
                      >
                        <SmartLink
                          href={page.slug}
                          target={emptyToUndefined(page.linkTarget)}
                          download={page.download}
                          aria-current={isCurrent ? 'page' : undefined}
                          data-track-id={`header-nav-${navIndex + 1}`}
                        >
                          {page.title}
                        </SmartLink>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right Side Buttons */}
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- render guard: a falsy actionButton must fall through to the language-switcher condition, so || is intended (not a nullish check) */}
          {(actionButton || (showLanguageSwitcher && languages.length > 1)) && (
            <div className="flex items-center gap-1">
              {showLanguageSwitcher && languages.length > 1 && (
                <LanguageSwitcherComponent
                  className="h-full hover:bg-transparent"
                  languages={languages}
                />
              )}

              {actionButton && (
                actionButton.download ? (
                  <DownloadButton
                    href={getDownloadUrl(actionButton.linkUrl, getFilenameFromUrl(actionButton.linkUrl))}
                    filename={getFilenameFromUrl(actionButton.linkUrl)}
                    variant="default"
                    size="sm"
                    className={getActionButtonClasses()}
                    data-track-id="header-action"
                  >
                    {actionButton.linkTitle}
                  </DownloadButton>
                ) : (
                  <Button
                    asChild
                    variant="default"
                    size="sm"
                    className={getActionButtonClasses()}
                  >
                    <SmartLink
                      href={actionButton.linkUrl}
                      target={actionButton.linkTarget}
                      download={actionButton.download}
                      data-track-id="header-action"
                    >
                      {actionButton.linkTitle}
                    </SmartLink>
                  </Button>
                )
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export { DesktopNavigation };
